import React, { useMemo, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Image,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { CalendarDays, LayoutGrid, Play, Tv } from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import {
  getLiveTvChannelsPage,
  getLiveTvPrograms,
  imageSource,
  JellyfinItem,
} from "../api/jellyfin";
import { useSession } from "../session/SessionProvider";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { FocusablePressable } from "../components/FocusablePressable";
import { colors , systemSansFont} from '../theme/tokens';

type Props = NativeStackScreenProps<RootStackParamList, "LiveTV">;

function floorHalfHour(date: Date) {
  const value = new Date(date);
  value.setMinutes(value.getMinutes() < 30 ? 0 : 30, 0, 0);
  return value;
}

function timeLabel(value?: string | Date) {
  const date = value instanceof Date ? value : value ? new Date(value) : null;
  return date
    ? date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    : "";
}

function guideTimeLabel(value: Date) {
  return value.toLocaleTimeString([], { hour: "numeric" });
}

function programPosition(
  program: JellyfinItem,
  windowStart: Date,
  windowEnd: Date,
  guideWidth: number,
) {
  const start = Date.parse(program.StartDate ?? "");
  const end = Date.parse(program.EndDate ?? "");
  const minimum = windowStart.getTime();
  const maximum = windowEnd.getTime();
  if (
    !Number.isFinite(start) ||
    !Number.isFinite(end) ||
    end <= minimum ||
    start >= maximum
  )
    return null;
  const duration = maximum - minimum;
  const clippedStart = Math.max(start, minimum);
  const clippedEnd = Math.min(end, maximum);
  return {
    left: Math.round(((clippedStart - minimum) / duration) * guideWidth),
    width: Math.max(
      1,
      Math.round(((clippedEnd - clippedStart) / duration) * guideWidth),
    ),
  };
}

function ChannelCard({
  channel,
  credentials,
  width,
  onPress,
}: {
  channel: JellyfinItem;
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  width: number;
  onPress: () => void;
}) {
  const program = channel.CurrentProgram;
  return (
    <FocusablePressable
      accessibilityLabel={`Watch ${channel.Name ?? "channel"}`}
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.channelCard,
        { width },
        (focused || hovered) && styles.channelCardActive,
        pressed && styles.pressed,
      ]}
    >
      <View
        style={[styles.channelArt, { width, height: Math.round(width * 0.56) }]}
      >
        <Image
          source={imageSource(
            credentials,
            program?.Id ? program : channel,
            program?.Id ? "Backdrop" : "Primary",
            width * 2,
          )}
          resizeMode="cover"
          style={{ width, height: Math.round(width * 0.56) }}
        />
        <View style={styles.channelPlay}>
          <Play color={colors.text} fill={colors.text} size={18} />
        </View>
      </View>
      <Text numberOfLines={1} style={styles.channelName}>
        {channel.Name ?? "Channel"}
      </Text>
      {program?.Name ? (
        <Text numberOfLines={1} style={styles.programName}>
          {program.Name}
        </Text>
      ) : null}
    </FocusablePressable>
  );
}

function ChannelPoster({
  channel,
  credentials,
  width,
  compact,
  onPress,
}: {
  channel: JellyfinItem;
  credentials: NonNullable<ReturnType<typeof useSession>["credentials"]>;
  width: number;
  compact: boolean;
  onPress: () => void;
}) {
  const height = Math.round(width * 1.5);
  return (
    <FocusablePressable
      accessibilityLabel={`Watch ${channel.Name ?? "channel"}`}
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.channelPoster,
        compact && styles.channelPosterCompact,
        { width },
        (focused || hovered) && styles.channelPosterActive,
        pressed && styles.pressed,
      ]}
    >
      <View style={[styles.channelPosterArt, { width, height }]}>
        <Image
          source={imageSource(credentials, channel, "Primary", width * 2)}
          resizeMode="contain"
          style={{ width, height }}
        />
      </View>
      <Text
        numberOfLines={1}
        style={[
          styles.channelPosterName,
          compact && styles.channelPosterNameCompact,
        ]}
      >
        {channel.Name ?? "Channel"}
      </Text>
      <Text
        style={[
          styles.channelPosterType,
          compact && styles.channelPosterTypeCompact,
        ]}
      >
        TvChannel
      </Text>
    </FocusablePressable>
  );
}

export function LiveTvScreen({ navigation }: Props) {
  const { width } = useReferenceWindowDimensions();
  const compact = width <= 760;
  const { credentials } = useSession();
  const bottomNavInset = useBottomNavInset();
  const watchChannel = (channelId?: string) => {
    if (channelId) navigation.navigate("Player", { itemId: channelId });
  };
  const [tab, setTab] = useState<"Guide" | "Channels">("Guide");
  const [windowOffset, setWindowOffset] = useState(0);
  const channelPageSize = 100;
  const channelsQuery = useInfiniteQuery({
    queryKey: ["live-tv", "channels"],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => getLiveTvChannelsPage(credentials!, pageParam, channelPageSize),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((total, page) => total + page.items.length, 0);
      return lastPage.items.length === channelPageSize && loaded < lastPage.totalCount
        ? loaded
        : undefined;
    },
    enabled: Boolean(credentials),
  });
  const channels = useMemo(() => {
    const seen = new Set<string>();
    return (channelsQuery.data?.pages ?? []).flatMap((page) => page.items).filter((channel) => {
      const key = channel.Id ?? `${channel.ChannelNumber ?? ""}:${channel.Name ?? ""}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [channelsQuery.data?.pages]);
  const windowHours = 4;
  const windowStart = useMemo(() => {
    const value = floorHalfHour(new Date());
    value.setHours(value.getHours() + windowOffset * windowHours);
    return value;
  }, [windowHours, windowOffset]);
  const windowEnd = useMemo(
    () => new Date(windowStart.getTime() + windowHours * 3600000),
    [windowHours, windowStart],
  );
  const canGoBackward = windowOffset * windowHours > -24;
  const canGoForward = windowOffset * windowHours < 24;
  const programsQuery = useQuery({
    queryKey: [
      "live-tv",
      "programs",
      channels.map((channel) => channel.Id).join(","),
      windowStart.toISOString(),
    ],
    queryFn: () =>
      getLiveTvPrograms(
        credentials!,
        channels.flatMap((channel) => (channel.Id ? [channel.Id] : [])),
        windowStart.toISOString(),
        windowEnd.toISOString(),
      ),
    enabled: Boolean(credentials && channels.length && tab === "Guide"),
  });
  const programsByChannel = useMemo(() => {
    const groups = new Map<string, JellyfinItem[]>();
    (programsQuery.data ?? []).forEach((program) => {
      if (program.ChannelId)
        groups.set(program.ChannelId, [
          ...(groups.get(program.ChannelId) ?? []),
          program,
        ]);
    });
    groups.forEach((values, key) =>
      groups.set(
        key,
        values.sort(
          (left, right) =>
            Date.parse(left.StartDate ?? "") -
            Date.parse(right.StartDate ?? ""),
        ),
      ),
    );
    return groups;
  }, [programsQuery.data]);
  if (!credentials) return null;
  if (channelsQuery.isLoading)
    return (
      <SafeAreaView style={styles.safe}>
        <MatureTopNav navigation={navigation} active="LiveTV" overlay />
        <View style={styles.state}>
          <Tv color="rgba(255,255,255,0.6)" size={48} />
          <Text style={styles.stateTitle}>Loading Live TV…</Text>
          <Text style={styles.stateCopy}>
            Getting your channels and program guide ready.
          </Text>
        </View>
      </SafeAreaView>
    );
  if (channelsQuery.isError)
    return (
      <SafeAreaView style={styles.safe}>
        <MatureTopNav navigation={navigation} active="LiveTV" overlay />
        <View style={styles.state}>
          <Tv color="rgba(255,255,255,0.6)" size={48} />
          <Text style={styles.stateTitle}>Live TV could not load</Text>
          <Text style={styles.stateCopy}>
            Jellyflix could not reach the channel guide. Check the server
            connection and try again.
          </Text>
          <FocusablePressable
            accessibilityLabel="Try loading Live TV again"
            onPress={() => void channelsQuery.refetch()}
            style={(focused, pressed, hovered) => [
              styles.retryButton,
              (focused || hovered) && styles.retryButtonActive,
              pressed && styles.pressed,
            ]}
          >
            <Text style={styles.retryText}>Try again</Text>
          </FocusablePressable>
        </View>
      </SafeAreaView>
    );
  if (!channels.length)
    return (
      <SafeAreaView style={styles.safe}>
        <MatureTopNav navigation={navigation} active="LiveTV" overlay />
        <View style={styles.state}>
          <Tv color="rgba(255,255,255,0.6)" size={48} />
          <Text style={styles.stateTitle}>Live TV Not Available</Text>
          <Text style={styles.stateCopy}>
            No Live TV channels are enabled. An administrator can enable
            imported channels in Remux.
          </Text>
        </View>
      </SafeAreaView>
    );
  const onNow = channels.filter((channel) => Boolean(channel.CurrentProgram));
  const cardWidth = compact ? 250 : 300;
  const channelColumns = compact
    ? 2
    : width >= 1800
      ? 8
      : width >= 1280
        ? 6
        : width >= 1000
          ? 5
          : 4;
  const channelGridWidth = compact
    ? 110
    : Math.min(
        190,
        Math.floor((width - 48 - 16 * (channelColumns - 1)) / channelColumns),
      );
  const guideWidth = Math.max(1, width - (compact ? 127 : 160));
  const timeSlotWidth = guideWidth / (windowHours * 2);
  return (
    <SafeAreaView style={styles.safe} edges={[]}>
      <FlatList
        data={tab === "Guide" ? channels : []}
        keyExtractor={(channel, index) =>
          channel.Id ?? `guide-channel-${index}`
        }
        contentContainerStyle={[styles.content, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}
        onEndReached={() => {
          if (channelsQuery.hasNextPage && !channelsQuery.isFetchingNextPage) {
            void channelsQuery.fetchNextPage();
          }
        }}
        onEndReachedThreshold={0.45}
        ListHeaderComponent={
          <>
            {onNow.length ? (
              <View style={styles.onNow}>
                <Text style={styles.heading}>On Now</Text>
                <FlatList
                  horizontal
                  data={onNow}
                  keyExtractor={(channel, index) =>
                    channel.Id ?? `now-${index}`
                  }
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.onNowRow}
                  renderItem={({ item }) => (
                    <ChannelCard
                      channel={item}
                      credentials={credentials}
                      width={cardWidth}
                      onPress={() => watchChannel(item.Id)}
                    />
                  )}
                />
              </View>
            ) : null}
            <View
              style={[
                styles.toolbar,
                compact && styles.toolbarCompact,
                compact && tab === "Channels" && styles.toolbarChannelsCompact,
              ]}
            >
              <View style={styles.tabs}>
                {(["Guide", "Channels"] as const).map((label) => (
                  <FocusablePressable
                    key={label}
                    onPress={() => setTab(label)}
                    style={(focused, pressed, hovered) => [
                      styles.tab,
                      tab === label && styles.tabSelected,
                      (focused || hovered) && styles.tabActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    {label === "Guide" ? (
                      <CalendarDays color={tab === label ? colors.text : "#a7a7a7"} size={16} />
                    ) : (
                      <LayoutGrid color={tab === label ? colors.text : "#a7a7a7"} size={16} />
                    )}
                    <Text style={[styles.tabText, tab !== label && styles.tabTextMuted]}>{label}</Text>
                  </FocusablePressable>
                ))}
              </View>
              {tab === "Guide" ? (
                <View style={[styles.windowControls, compact && styles.windowControlsCompact]}>
                  <FocusablePressable
                    accessibilityLabel="Earlier programs"
                    onPress={() =>
                      canGoBackward && setWindowOffset((value) => value - 1)
                    }
                    style={(focused, pressed, hovered) => [
                      styles.windowButton,
                      compact && styles.windowButtonCompact,
                      !canGoBackward && styles.windowButtonDisabled,
                      canGoBackward &&
                        (focused || hovered) &&
                        styles.windowButtonActive,
                      pressed && canGoBackward && styles.pressed,
                    ]}
                  >
                    <Text style={styles.windowText}>← Earlier</Text>
                  </FocusablePressable>
                  <FocusablePressable
                    accessibilityLabel="Go to now"
                    onPress={() => setWindowOffset(0)}
                    style={(focused, pressed, hovered) => [
                      styles.nowButton,
                      compact && styles.nowButtonCompact,
                      (focused || hovered) && styles.nowButtonActive,
                      pressed && styles.pressed,
                    ]}
                  >
                    <Text style={styles.nowText}>Now</Text>
                  </FocusablePressable>
                  <FocusablePressable
                    accessibilityLabel="Later programs"
                    onPress={() =>
                      canGoForward && setWindowOffset((value) => value + 1)
                    }
                    style={(focused, pressed, hovered) => [
                      styles.windowButton,
                      compact && styles.windowButtonCompact,
                      !canGoForward && styles.windowButtonDisabled,
                      canGoForward &&
                        (focused || hovered) &&
                        styles.windowButtonActive,
                      pressed && canGoForward && styles.pressed,
                    ]}
                  >
                    <Text style={styles.windowText}>Later →</Text>
                  </FocusablePressable>
                </View>
              ) : null}
            </View>
            {tab === "Guide" ? (
              <View style={styles.timeHeader}>
                <View
                  style={[
                    styles.channelColumn,
                    compact && styles.channelColumnCompact,
                  ]}
                />
                <View style={styles.timeLabels}>
                  {Array.from({ length: windowHours * 2 }, (_, index) => (
                    <Text
                      key={index}
                      style={[styles.timeText, { width: timeSlotWidth }]}
                    >
                      {guideTimeLabel(
                        new Date(windowStart.getTime() + index * 1800000),
                      )}
                    </Text>
                  ))}
                </View>
              </View>
            ) : (
              <FlatList
                key={`channels-${channelColumns}`}
                scrollEnabled={false}
                data={channels}
                numColumns={channelColumns}
                keyExtractor={(channel, index) =>
                  channel.Id ?? `channel-${index}`
                }
                columnWrapperStyle={[
                  styles.channelGridRow,
                  compact
                    ? styles.channelGridRowCompact
                    : styles.channelGridRowWide,
                ]}
                contentContainerStyle={[
                  styles.channelGrid,
                  compact && styles.channelGridCompact,
                ]}
                renderItem={({ item }) => (
                  <ChannelPoster
                    channel={item}
                    credentials={credentials}
                    width={channelGridWidth}
                    compact={compact}
                    onPress={() => watchChannel(item.Id)}
                  />
                )}
              />
            )}
          </>
        }
        renderItem={({ item: channel }) => (
          <View style={[styles.guideRow, compact && styles.guideRowCompact]}>
            <FocusablePressable
              accessibilityLabel={`Watch ${channel.Name ?? "channel"}`}
              onPress={() => watchChannel(channel.Id)}
              style={(focused, pressed, hovered) => [
                styles.channelColumn,
                compact && styles.channelColumnCompact,
                (focused || hovered) && styles.guideChannelActive,
                pressed && styles.pressed,
              ]}
            >
              <Image
                source={imageSource(credentials, channel, "Primary", 120)}
                resizeMode="contain"
                style={[styles.guideLogo, compact && styles.guideLogoCompact]}
              />
              <View style={styles.guideChannelCopy}>
                <Text numberOfLines={1} style={styles.guideChannelName}>
                  {channel.Name ?? "Channel"}
                </Text>
                {channel.ChannelNumber ? (
                  <Text style={styles.guideChannelNumber}>
                    {channel.ChannelNumber}
                  </Text>
                ) : null}
              </View>
            </FocusablePressable>
            <View style={styles.programLayer}>
              {(programsByChannel.get(channel.Id ?? "") ?? []).map(
                (program, index) => {
                  const position = programPosition(
                    program,
                    windowStart,
                    windowEnd,
                    guideWidth,
                  );
                  if (!position) return null;
                  const now = Date.now();
                  const live =
                    Date.parse(program.StartDate ?? "") <= now &&
                    Date.parse(program.EndDate ?? "") > now;
                  return (
                    <FocusablePressable
                      key={program.Id ?? `program-${index}`}
                      accessibilityLabel={`${program.Name ?? "Program"} on ${channel.Name ?? "channel"}`}
                      onPress={() =>
                        watchChannel(program.ChannelId ?? channel.Id)
                      }
                      style={(focused, pressed, hovered) => [
                        styles.program,
                        { left: position.left, width: position.width },
                        live && styles.programLive,
                        (focused || hovered) && styles.programActive,
                        pressed && styles.pressed,
                      ]}
                    >
                      <Text numberOfLines={1} style={styles.programTitle}>
                        {program.Name ?? "No information"}
                      </Text>
                      {live ? (
                        <Text style={styles.programLiveText}>LIVE</Text>
                      ) : null}
                    </FocusablePressable>
                  );
                },
              )}
            </View>
          </View>
        )}
        ListEmptyComponent={
          tab === "Guide" && programsQuery.isLoading ? (
            <ActivityIndicator color={colors.brand} style={styles.loader} />
          ) : null
        }
        ListFooterComponent={channelsQuery.isFetchingNextPage ? (
          <View style={styles.pageLoader}>
            <ActivityIndicator color={colors.brand} />
            <Text style={styles.pageLoaderText}>Loading more channels…</Text>
          </View>
        ) : null}
      />
      <MatureTopNav navigation={navigation} active="LiveTV" overlay />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  content: { paddingTop: 76, paddingBottom: 60 },
  state: {
    flex: 1,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
    gap: 14,
  },
  stateTitle: {
    color: colors.text,
    fontSize: 24,
    lineHeight: 30,
    fontWeight: "800",
  },
  stateCopy: {
    maxWidth: 460,
    color: "rgba(255,255,255,0.62)",
    fontSize: 15,
    lineHeight: 22,
    textAlign: "center",
  },
  retryButton: {
    minHeight: 40,
    paddingHorizontal: 18,
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  retryButtonActive: { opacity: 0.88, transform: [{ scale: 1.03 }] },
  retryText: {
    color: colors.text,
    fontSize: 13,
    lineHeight: 19,
    fontWeight: "700",
  },
  onNow: { marginTop: 16 },
  heading: {
    marginHorizontal: 24,
    marginBottom: 12,
    color: colors.text,
    fontSize: 19,
    lineHeight: 25,
    fontWeight: "800",
  },
  onNowRow: { paddingHorizontal: 24, gap: 12 },
  channelCard: { paddingBottom: 12, borderRadius: 7 },
  channelCardActive: {
    backgroundColor: "rgba(255,255,255,0.08)",
    transform: [{ scale: 1.02 }],
  },
  channelPoster: { flexGrow: 0, flexShrink: 0, paddingBottom: 4 },
  channelPosterCompact: { paddingBottom: 1 },
  channelPosterActive: { transform: [{ scale: 1.02 }] },
  channelPosterArt: {
    overflow: "hidden",
    borderRadius: 8,
    backgroundColor: "#202020",
  },
  channelPosterName: {
    marginTop: 7,
    marginHorizontal: 2,
    color: colors.text,
    fontSize: 13,
    lineHeight: 18,
  },
  channelPosterNameCompact: { fontFamily: systemSansFont,  marginTop: 5, fontSize: 12, lineHeight: 16 },
  channelPosterType: {
    marginHorizontal: 2,
    color: "rgba(255,255,255,0.5)",
    fontSize: 12,
    lineHeight: 15,
  },
  channelPosterTypeCompact: { fontFamily: systemSansFont,  fontSize: 10, lineHeight: 14 },
  channelArt: {
    overflow: "hidden",
    borderRadius: 7,
    backgroundColor: "#252525",
  },
  channelPlay: {
    position: "absolute",
    right: 10,
    bottom: 10,
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0,0,0,0.74)",
  },
  channelName: {
    marginTop: 7,
    color: colors.text,
    fontSize: 14,
    lineHeight: 19,
    fontWeight: "800",
  },
  programName: {
    marginTop: 2,
    color: "rgba(255,255,255,0.62)",
    fontSize: 12,
    lineHeight: 16,
  },
  toolbar: {
    minHeight: 71,
    marginTop: 8,
    paddingHorizontal: 24,
    paddingTop: 13,
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 14,
  },
  toolbarCompact: {
    minHeight: 88,
    marginTop: 48,
    paddingHorizontal: 17,
    paddingTop: 21,
    alignItems: "flex-start",
    justifyContent: "flex-start",
    flexDirection: "row",
    gap: 16,
  },
  toolbarChannelsCompact: { minHeight: 82, marginTop: 38 },
  tabs: {
    padding: 2,
    borderRadius: 7,
    flexDirection: "row",
    backgroundColor: "#111",
  },
  tab: {
    minHeight: 30,
    paddingHorizontal: 12,
    borderRadius: 5,
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
  },
  tabSelected: { backgroundColor: "#2a2a2a" },
  tabActive: { backgroundColor: "#333" },
  tabText: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 18,
    fontWeight: "600",
  },
  tabTextMuted: { fontFamily: systemSansFont,  color: "#a7a7a7", fontWeight: "400" },
  windowControls: { flexDirection: "row", gap: 8 },
  windowControlsCompact: { alignItems: "center", transform: [{ translateY: -9 }] },
  windowButton: {
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: "#111",
  },
  windowButtonCompact: { width: 64, paddingHorizontal: 12, paddingVertical: 6, alignItems: "center" },
  windowButtonActive: { backgroundColor: "#222" },
  windowButtonDisabled: { opacity: 0.3 },
  windowText: { fontFamily: systemSansFont,  color: colors.text, fontSize: 13, lineHeight: 18 },
  nowButton: {
    minHeight: 34,
    paddingHorizontal: 14,
    borderRadius: 6,
    justifyContent: "center",
    backgroundColor: colors.brand,
  },
  nowButtonCompact: { width: 52, paddingHorizontal: 0, alignItems: "center" },
  nowButtonActive: { opacity: 0.9 },
  nowText: {
    color: colors.text,
    fontSize: 13,
    lineHeight: 18,
    fontWeight: "800",
  },
  timeHeader: {
    height: 42,
    flexDirection: "row",
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: "rgba(255,255,255,0.08)",
    backgroundColor: "#181818",
  },
  channelColumn: {
    width: 160,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    gap: 9,
    borderRightWidth: 1,
    borderRightColor: "rgba(255,255,255,0.08)",
  },
  channelColumnCompact: { width: 127, paddingHorizontal: 8, gap: 8 },
  guideChannelActive: { backgroundColor: "rgba(255,255,255,0.08)" },
  timeLabels: {
    flex: 1,
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
  },
  timeText: {
    flexShrink: 0,
    color: "rgba(255,255,255,0.54)",
    fontSize: 11,
    lineHeight: 15,
    textAlign: "center",
  },
  guideRow: {
    minHeight: 80,
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "rgba(255,255,255,0.07)",
  },
  guideRowCompact: { minHeight: 64 },
  guideLogo: { width: 60, height: 42 },
  guideLogoCompact: { width: 32, height: 32 },
  guideChannelName: {
    flex: 1,
    color: colors.text,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "400",
  },
  guideChannelCopy: { flex: 1, minWidth: 0 },
  guideChannelNumber: {
    color: "rgba(255,255,255,0.42)",
    fontSize: 10,
    lineHeight: 13,
  },
  programLayer: { flex: 1, position: "relative", overflow: "hidden" },
  program: {
    position: "absolute",
    top: 4,
    bottom: 4,
    paddingHorizontal: 10,
    borderRadius: 5,
    justifyContent: "center",
    backgroundColor: "#252525",
  },
  programLive: { backgroundColor: "rgba(229,9,20,0.2)" },
  programLiveText: {
    marginTop: 2,
    color: colors.brand,
    fontSize: 10,
    lineHeight: 13,
    fontWeight: "700",
  },
  programActive: {
    backgroundColor: "#393939",
    borderWidth: 1,
    borderColor: colors.text,
  },
  programTitle: {
    color: colors.text,
    fontSize: 12,
    lineHeight: 16,
    fontWeight: "700",
  },
  channelGrid: { paddingTop: 12 },
  channelGridCompact: { paddingTop: 0 },
  channelGridRow: { marginBottom: 16 },
  channelGridRowWide: { marginHorizontal: 24, gap: 45 },
  channelGridRowCompact: { marginHorizontal: 16, gap: 77 },
  loader: { marginTop: 60 },
  pageLoader: { minHeight: 74, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 10 },
  pageLoaderText: { color: "rgba(255,255,255,0.62)", fontFamily: systemSansFont, fontSize: 13, lineHeight: 18 },
  pressed: { opacity: 0.76 },
});
